import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { eventHeads } from './projection';
import type { ClinicalEpisode, ClinicalEvent } from './types';
export function clinicalValue(value: unknown): string {
  if (value === null || value === undefined || value === '') return 'Not recorded';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (Array.isArray(value)) return value.map(clinicalValue).join('; ') || 'Not recorded';
  if (typeof value === 'object')
    return (
      Object.entries(value)
        .map(([k, v]) => `${k.replaceAll('_', ' ')}: ${clinicalValue(v)}`)
        .join('; ') || 'Not recorded'
    );
  return String(value);
}
export function offlineTransferRows(events: ClinicalEvent[], episodeId: string, encounterId: string, now = Date.now()) {
  return eventHeads(events.filter((e) => e.episode_id === episodeId))
    .filter(
      (e) =>
        (e.encounter_id === encounterId || /^(PRENATAL\.|LAB\.|US\.)/.test(e.event_type)) &&
        e.status !== 'STRUCK' &&
        Date.parse(e.occurred_at) <= now,
    )
    .sort((a, b) => Date.parse(a.occurred_at) - Date.parse(b.occurred_at) || a.sequence - b.sequence);
}
export function OfflineTransferPreview({
  episode,
  events,
  encounterId,
  timeZone,
  patientLabel,
  cachedAt,
  edd,
}: {
  episode: ClinicalEpisode;
  events: ClinicalEvent[];
  encounterId: string;
  timeZone: string;
  patientLabel?: string;
  cachedAt?: string;
  edd?: string | null;
}) {
  const [shown, setShown] = useState(false);
  const rows = offlineTransferRows(events, episode.id, encounterId);
  return (
    <section className="space-y-3 rounded-lg border p-4" aria-label="Offline transfer evidence">
      <h3 className="font-semibold">Transfer evidence while offline</h3>
      <p>
        {patientLabel ?? `Patient ${episode.patient_id}`} · EDD {edd ?? 'Not recorded'}
        {cachedAt
          ? ` · Last downloaded ${new Intl.DateTimeFormat(undefined, { dateStyle: 'short', timeStyle: 'short', timeZone }).format(new Date(cachedAt))}`
          : ''}
      </p>
      <p>
        On-device observations only; other devices may have newer evidence. Pending and unsigned entries retain their
        labels. Reconnect to prepare a complete packet, review the current record and save or sign a report.
      </p>
      <Button type="button" variant="outline" onClick={() => setShown(!shown)}>
        {shown ? 'Hide cached evidence' : 'Show cached encounter evidence'}
      </Button>
      {shown && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <caption>
              Episode {episode.id} · encounter {encounterId} · {timeZone}
            </caption>
            <thead>
              <tr>
                <th>Time / subject</th>
                <th>Observation</th>
                <th>Findings</th>
                <th>Review / source</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((e) => (
                <tr key={e.id} className="border-b align-top">
                  <td className="p-2">
                    {new Intl.DateTimeFormat(undefined, { dateStyle: 'short', timeStyle: 'short', timeZone }).format(
                      new Date(e.occurred_at),
                    )}
                    <br />
                    {episode.subjects.find((s) => s.id === e.subject_id)?.label ?? e.subject_id}
                  </td>
                  <td className="p-2">{e.event_type}</td>
                  <td className="p-2 whitespace-pre-wrap">{clinicalValue(e.payload)}</td>
                  <td className="p-2">
                    {e.status} · {e.signed ? 'Signed' : 'Unsigned'}
                    <br />
                    {e.author_initials}
                    <br />
                    <span className="break-all">{e.id}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!rows.length && <p>No cached observations for this encounter.</p>}
        </div>
      )}
    </section>
  );
}
